import React, { useState, useEffect } from 'react';
import { useFieldMode } from '../context/FieldModeContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Progress } from './ui/progress';
import {
  Smartphone, Wifi, WifiOff, RefreshCw, CloudOff, Cloud, Check,
  AlertTriangle, Clock, ChevronUp, ChevronDown, Loader2, X, ArrowLeft
} from 'lucide-react';

// Sync Status Banner - Shows at top of screen in Field Mode
export const SyncStatusBanner = () => {
  const { isOnline, syncStatus, forceSync } = useFieldMode();
  const [expanded, setExpanded] = useState(false);

  if (!syncStatus.pendingCount && isOnline) return null;

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 transition-all ${
      isOnline ? 'bg-blue-600' : 'bg-orange-500'
    }`}>
      <div 
        className="flex items-center justify-between px-4 py-2 text-white cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {isOnline ? (
            <Cloud className="w-4 h-4" />
          ) : (
            <CloudOff className="w-4 h-4" />
          )}
          <span className="text-sm font-medium">
            {isOnline 
              ? `${syncStatus.pendingCount} items pending sync`
              : 'Offline Mode - Data saved locally'
            }
          </span>
        </div>
        <div className="flex items-center gap-2">
          {syncStatus.isSyncing && (
            <Loader2 className="w-4 h-4 animate-spin" />
          )}
          {expanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-3 space-y-2 bg-black/10">
          <div className="flex items-center justify-between text-sm text-white/90">
            <span>Pending submissions:</span>
            <span className="font-medium">{syncStatus.pendingCount}</span>
          </div>
          {syncStatus.failedCount > 0 && (
            <div className="flex items-center justify-between text-sm text-white/90">
              <span>Failed (will retry):</span>
              <span className="font-medium">{syncStatus.failedCount}</span>
            </div>
          )}
          {syncStatus.lastSync && (
            <div className="flex items-center justify-between text-sm text-white/90">
              <span>Last sync:</span>
              <span>{new Date(syncStatus.lastSync).toLocaleTimeString()}</span>
            </div>
          )}
          {isOnline && (
            <Button 
              size="sm" 
              variant="secondary" 
              className="w-full mt-2"
              onClick={(e) => { e.stopPropagation(); forceSync(); }}
              disabled={syncStatus.isSyncing}
            >
              {syncStatus.isSyncing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync Now
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Field Mode Toggle Button
export const FieldModeToggle = ({ showLabel = true }) => {
  const { isFieldMode, toggleFieldMode, isOnline } = useFieldMode();

  return (
    <div className="flex items-center gap-3">
      {showLabel && (
        <div className="flex items-center gap-2">
          <Smartphone className={`w-4 h-4 ${isFieldMode ? 'text-blue-600' : 'text-gray-400'}`} />
          <span className="text-sm font-medium">Field Mode</span>
        </div>
      )}
      <Switch
        checked={isFieldMode}
        onCheckedChange={toggleFieldMode}
        data-testid="field-mode-toggle"
      />
      {isFieldMode && (
        <Badge variant={isOnline ? 'default' : 'warning'} className="ml-2">
          {isOnline ? (
            <><Wifi className="w-3 h-3 mr-1" /> Online</>
          ) : (
            <><WifiOff className="w-3 h-3 mr-1" /> Offline</>
          )}
        </Badge>
      )}
    </div>
  );
};

// Offline Indicator - Small badge for header
export const OfflineIndicator = () => {
  const { isOnline, syncStatus } = useFieldMode();

  if (isOnline && !syncStatus.pendingCount) return null;

  return (
    <div className="flex items-center gap-2">
      {!isOnline && (
        <Badge variant="warning" className="gap-1">
          <WifiOff className="w-3 h-3" />
          Offline
        </Badge>
      )}
      {syncStatus.pendingCount > 0 && (
        <Badge variant="secondary" className="gap-1">
          <Clock className="w-3 h-3" />
          {syncStatus.pendingCount} pending
        </Badge>
      )}
    </div>
  );
};

// Quick Action Card for Field Mode Dashboard
export const QuickActionCard = ({ icon: Icon, title, description, onClick, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-500 hover:bg-blue-600',
    red: 'bg-red-500 hover:bg-red-600',
    yellow: 'bg-yellow-500 hover:bg-yellow-600',
    green: 'bg-green-500 hover:bg-green-600',
    purple: 'bg-purple-500 hover:bg-purple-600'
  };

  return (
    <button
      onClick={onClick}
      className={`w-full p-6 rounded-2xl text-white transition-all active:scale-95 ${colorClasses[color]}`}
      data-testid={`quick-action-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="w-10 h-10 mb-3" />
      <h3 className="text-lg font-bold">{title}</h3>
      <p className="text-sm text-white/80 mt-1">{description}</p>
    </button>
  );
};

// Mobile Step Indicator for multi-step forms
export const StepIndicator = ({ currentStep, totalSteps, steps = [] }) => {
  // Handle both array of strings and array of objects with {number, title}
  const getStepTitle = (index) => {
    const step = steps[index];
    if (!step) return '';
    if (typeof step === 'string') return step;
    if (typeof step === 'object' && step.title) return step.title;
    return '';
  };

  const stepsCount = totalSteps || steps.length || 1;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-600">
          Step {currentStep} of {stepsCount}
        </span>
        <span className="text-sm text-gray-500">
          {getStepTitle(currentStep - 1)}
        </span>
      </div>
      <Progress value={(currentStep / stepsCount) * 100} className="h-2" />
      <div className="flex justify-between mt-2">
        {Array.from({ length: stepsCount }, (_, i) => (
          <div
            key={i}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
              i + 1 < currentStep
                ? 'bg-green-500 text-white'
                : i + 1 === currentStep
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-500'
            }`}
          >
            {i + 1 < currentStep ? <Check className="w-4 h-4" /> : i + 1}
          </div>
        ))}
      </div>
    </div>
  );
};

// Mobile Form Section - Collapsible
export const MobileFormSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Card className="mb-4">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-500" />}
          <span className="font-medium">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <CardContent className="pt-0 pb-4 px-4">
          {children}
        </CardContent>
      )}
    </Card>
  );
};

// Large Touch-Friendly Button
export const TouchButton = ({ 
  children, 
  variant = 'primary', 
  size = 'lg', 
  fullWidth = true,
  icon: Icon,
  className = '',
  ...props 
}) => {
  const variants = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-800',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    success: 'bg-green-600 hover:bg-green-700 text-white',
    outline: 'border-2 border-gray-300 hover:border-gray-400 text-gray-700'
  };

  const sizes = {
    sm: 'py-2 px-4 text-sm',
    md: 'py-3 px-6 text-base',
    lg: 'py-4 px-8 text-lg min-h-[56px]'
  };

  return (
    <button
      className={`
        rounded-xl font-medium transition-all active:scale-95
        flex items-center justify-center gap-2
        ${variants[variant]}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {Icon && <Icon className="w-5 h-5" />}
      {children}
    </button>
  );
};

// Auto-save Draft Indicator
export const DraftIndicator = ({ lastSaved, isSaving }) => {
  if (!lastSaved && !isSaving) return null;

  return (
    <div className="flex items-center gap-2 text-sm text-gray-500">
      {isSaving ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Saving draft...</span>
        </>
      ) : (
        <>
          <Check className="w-4 h-4 text-green-500" />
          <span>Draft saved {new Date(lastSaved).toLocaleTimeString()}</span>
        </>
      )}
    </div>
  );
};

// Submission Success Modal
export const SubmissionSuccess = ({ isOpen, onClose, isOffline, itemType }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-sm animate-in zoom-in-95">
        <CardContent className="pt-6 text-center">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
            isOffline ? 'bg-yellow-100' : 'bg-green-100'
          }`}>
            {isOffline ? (
              <CloudOff className="w-8 h-8 text-yellow-600" />
            ) : (
              <Check className="w-8 h-8 text-green-600" />
            )}
          </div>
          <h3 className="text-xl font-bold mb-2">
            {isOffline ? 'Saved Offline' : 'Submitted Successfully'}
          </h3>
          <p className="text-gray-600 mb-6">
            {isOffline 
              ? `Your ${itemType} has been saved locally and will sync when you're back online.`
              : `Your ${itemType} has been submitted successfully.`
            }
          </p>
          <TouchButton onClick={onClose}>
            Continue
          </TouchButton>
        </CardContent>
      </Card>
    </div>
  );
};

// Mobile Form Header with back button
export const MobileFormHeader = ({ title, subtitle, onBack, icon: Icon }) => {
  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-4 sticky top-0 z-10">
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-white/20 transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-3 flex-1">
          {Icon && (
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Icon className="w-5 h-5" />
            </div>
          )}
          <div>
            <h1 className="text-xl font-bold">{title}</h1>
            {subtitle && <p className="text-blue-100 text-sm">{subtitle}</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

// Auto-save indicator (alias for DraftIndicator)
export const AutoSaveIndicator = DraftIndicator;

export default {
  SyncStatusBanner,
  FieldModeToggle,
  OfflineIndicator,
  QuickActionCard,
  StepIndicator,
  MobileFormSection,
  TouchButton,
  DraftIndicator,
  AutoSaveIndicator,
  MobileFormHeader,
  SubmissionSuccess
};
